/**
 * Order-volume billing (monthly quota UI) applies to Starter and Pro only.
 * @see GET /api/billing/order-volume — `eligible: false` for other plans.
 */

import {
  isLegacyBillingUserType,
  isLegacyOrDailyBillingVariant,
  resolveBillingScreenVariant
} from './billingScreenVariant.js'

/** Sidebar order meter + billing Usage tab. Off while unused. */
export const ORDER_VOLUME_UI_ENABLED = false

/** Tenant-specific Pro monthly order caps (default Pro cap is 10,000). */
export const ELEVATED_PRO_ORDER_LIMIT_BY_TENANT_ID = {}

/** Tenants that must not see monthly order caps anywhere in the UI. */
export const TENANT_IDS_WITHOUT_ORDER_CAP = {
  '1377998158123013': true
}

export function tenantIdFromValue (value) {
  if (value == null || value === '') return null
  return String(value).trim()
}

/** Read tenant id from GET /api/identity/settings (or similar) body. */
export function tenantIdFromIdentityBody (body) {
  if (!body || typeof body !== 'object') return null
  if (body.tenantId != null) return tenantIdFromValue(body.tenantId)
  if (body.tenant_id != null) return tenantIdFromValue(body.tenant_id)
  if (body.tenant && typeof body.tenant === 'object') {
    if (body.tenant.id != null) return tenantIdFromValue(body.tenant.id)
    if (body.tenant.tenantId != null) return tenantIdFromValue(body.tenant.tenantId)
    if (body.tenant.tenant_id != null) return tenantIdFromValue(body.tenant.tenant_id)
  }
  return null
}

/** Read tenant id from GET /api/billing/order-volume response. */
export function tenantIdFromOrderVolumeBody (body) {
  if (!body || typeof body !== 'object') return null
  if (body.tenantId != null) return tenantIdFromValue(body.tenantId)
  if (body.tenant_id != null) return tenantIdFromValue(body.tenant_id)
  return null
}

/** Store tenant id from order-volume on a component and optional Vue root. */
export function syncTenantIdFromOrderVolume (ctx, orderVolumeBody) {
  var id = tenantIdFromOrderVolumeBody(orderVolumeBody)
  if (!id || !ctx) return id
  ctx.tenantId = id
  if (ctx.$root) ctx.$root.tenantId = id
  return id
}

export function tenantHasNoOrderCap (tenantId) {
  var id = tenantIdFromValue(tenantId)
  return !!(id && TENANT_IDS_WITHOUT_ORDER_CAP[id])
}

export function isTenantWithoutOrderCap (opts) {
  return tenantHasNoOrderCap(tenantIdFromOpts(opts))
}

export function proMonthlyOrderLimitForTenant (tenantId) {
  if (tenantHasNoOrderCap(tenantId)) return null
  var id = tenantIdFromValue(tenantId)
  if (id && ELEVATED_PRO_ORDER_LIMIT_BY_TENANT_ID[id] != null) {
    return ELEVATED_PRO_ORDER_LIMIT_BY_TENANT_ID[id]
  }
  return 10000
}

export function packageKeyFromDetails (packageDetails) {
  if (!packageDetails) return ''
  var raw =
    packageDetails.packageKey != null
      ? packageDetails.packageKey
      : packageDetails.package_key
  return String(raw == null ? '' : raw).trim().toUpperCase()
}

export function monthlyOrderLimitForPackageKey (packageKey, tenantId) {
  if (tenantHasNoOrderCap(tenantId)) return null
  var pk = String(packageKey || '').trim().toUpperCase()
  if (pk === 'PRO' || pk === 'PRO_ANNUAL') return proMonthlyOrderLimitForTenant(tenantId)
  if (pk === 'STARTER') return 2000
  if (pk === 'FLAT_YEARLY') return null
  return null
}

function tenantIdFromOpts (opts) {
  opts = opts || {}
  var fromOrderVolume = tenantIdFromOrderVolumeBody(opts.orderVolumeBody)
  if (fromOrderVolume) return fromOrderVolume
  if (opts.tenantId != null) return tenantIdFromValue(opts.tenantId)
  return tenantIdFromIdentityBody(opts.identityBody)
}

/** Apply tenant-specific Pro cap to API/fallback monthly order volume. */
export function applyTenantProOrderLimit (monthlyOrderVolume, packageDetails, tenantId) {
  if (!monthlyOrderVolume || monthlyOrderVolume.limit == null) return monthlyOrderVolume
  if (tenantHasNoOrderCap(tenantId)) return monthlyOrderVolume
  var packageKey = packageKeyFromDetails(packageDetails)
  if (packageKey !== 'PRO' && packageKey !== 'PRO_ANNUAL') return monthlyOrderVolume
  var cap = proMonthlyOrderLimitForTenant(tenantId)
  if (Number(monthlyOrderVolume.limit) === cap) return monthlyOrderVolume
  var used = Number(monthlyOrderVolume.used)
  if (isNaN(used)) used = 0
  var remaining = Math.max(0, cap - used)
  var percentUsed = cap > 0 ? Math.min(100, Math.round((used / cap) * 1000) / 10) : 0
  return Object.assign({}, monthlyOrderVolume, {
    limit: cap,
    remaining: remaining,
    percentUsed: percentUsed
  })
}

/** True for the modern subscription plans (not Standard / legacy / daily). */
export function isStarterOrProPlanKey (packageKey) {
  var pk = String(packageKey || '').trim().toUpperCase()
  return pk === 'STARTER' || pk === 'PRO' || pk === 'PRO_ANNUAL' || pk === 'FLAT_YEARLY'
}

export function isFlatYearlyPlanKey (packageKey) {
  return String(packageKey || '').trim().toUpperCase() === 'FLAT_YEARLY'
}

/** Pro plan: storefronts included in the $49 base price. */
export const PRO_INCLUDED_STOREFRONTS = 5

/** Pro plan base monthly price (USD). */
export const PRO_BASE_MONTHLY_PRICE = 49

/** Starter plan flat monthly price (USD). */
export const STARTER_MONTHLY_PRICE = 19

/** Starter plan: maximum connected storefronts (Seller IDs). */
export const STARTER_MAX_STOREFRONTS = 2

/** Per-storefront add-on rate for storefront index n (6+). */
export function proStorefrontUnitRate (storefrontNumber) {
  var n = Number(storefrontNumber)
  if (isNaN(n) || n <= PRO_INCLUDED_STOREFRONTS) return 0
  if (n <= 15) return 19
  if (n <= 30) return 14
  if (n <= 60) return 10
  return 7
}

/** Total Pro monthly price for a connected storefront count. */
export function proMonthlyPriceForStorefrontCount (count) {
  var n = Math.max(0, Math.floor(Number(count) || 0))
  var total = PRO_BASE_MONTHLY_PRICE
  for (var i = PRO_INCLUDED_STOREFRONTS + 1; i <= n; i++) {
    total += proStorefrontUnitRate(i)
  }
  return total
}

/** Resolve connected storefront count from period_changes body (snake or camelCase). */
export function connectedStorefrontsCountFromPeriodChanges (body) {
  if (!body || typeof body !== 'object') return null
  var raw =
    body.connected_storefronts_count != null
      ? body.connected_storefronts_count
      : body.connectedStorefrontsCount
  if (raw == null || raw === '' || isNaN(Number(raw))) return null
  return Math.max(0, Math.floor(Number(raw)))
}

/** Sidebar / Usage meter plan badge — STARTER or PRO (not TRIAL). */
export function storefrontMeterPlanBadge (packageDetails, orderVolumeBody, opts) {
  var pk = resolveOrderVolumePlanKey(packageDetails, orderVolumeBody, opts)
  if (pk === 'PRO' || pk === 'PRO_ANNUAL') return 'PRO'
  if (pk === 'STARTER') return 'STARTER'
  if (pk === 'FLAT_YEARLY') return 'FLAT YEARLY'
  return pk || 'PLAN'
}

/** Whether tenant is on Pro (not Starter) for storefront meter pricing. */
export function isProOrderVolumePlan (packageDetails, orderVolumeBody, opts) {
  var pk = resolveOrderVolumePlanKey(packageDetails, orderVolumeBody, opts)
  return pk === 'PRO' || pk === 'PRO_ANNUAL'
}

/** Whether tenant is on Starter (new billing). */
export function isStarterOrderVolumePlan (packageDetails, orderVolumeBody, opts) {
  return resolveOrderVolumePlanKey(packageDetails, orderVolumeBody, opts) === 'STARTER'
}

/** Starter is capped at {@link STARTER_MAX_STOREFRONTS}; Pro and others may add freely in UI. */
export function canAddStorefrontForPlan (packageDetails, connectedCount, opts) {
  if (!isStarterOrderVolumePlan(packageDetails, opts && opts.orderVolumeBody, opts)) return true
  var n = Math.max(0, Math.floor(Number(connectedCount) || 0))
  return n < STARTER_MAX_STOREFRONTS
}

function planNameImpliesStarterOrPro (name) {
  var n = String(name || '').toLowerCase().trim()
  if (!n) return null
  if (n.indexOf('standard') >= 0 || n.indexOf('legacy') >= 0 || n.indexOf('daily') >= 0) return null
  if (n.indexOf('flat yearly') >= 0) return 'FLAT_YEARLY'
  if (n.indexOf('starter') >= 0) return 'STARTER'
  if (n.indexOf('pro annual') >= 0) return 'PRO_ANNUAL'
  if (n === 'pro' || n.indexOf('pro plan') >= 0 || /^pro\b/.test(n)) return 'PRO'
  return null
}

/**
 * Resolve plan key for order-volume UI (allowlist: STARTER | PRO only).
 * @param {object} [opts] — `subscriptionSummary`
 */
export function resolveOrderVolumePlanKey (packageDetails, orderVolumeBody, opts) {
  opts = opts || {}
  var plan = orderVolumeBody && orderVolumeBody.plan
  if (plan) {
    var fromPlanKey = plan.packageKey != null ? plan.packageKey : plan.package_key
    if (fromPlanKey != null && String(fromPlanKey).trim() !== '') {
      return String(fromPlanKey).trim().toUpperCase()
    }
    var fromPlanName = planNameImpliesStarterOrPro(plan.name || plan.planName)
    if (fromPlanName) return fromPlanName
  }
  var fromPkg = packageKeyFromDetails(packageDetails)
  if (fromPkg) return fromPkg
  if (packageDetails) {
    var fromPkgName = planNameImpliesStarterOrPro(
      packageDetails.name || packageDetails.packageName
    )
    if (fromPkgName) return fromPkgName
  }
  var summary = opts.subscriptionSummary
  if (summary && summary.planName) {
    var fromSummary = planNameImpliesStarterOrPro(summary.planName)
    if (fromSummary) return fromSummary
  }
  return ''
}

/** Client-side Starter / Pro — strict package key / plan name only (no price heuristics). */
export function isStarterOrProPackage (packageDetails, orderVolumeBody, opts) {
  return isStarterOrProPlanKey(resolveOrderVolumePlanKey(packageDetails, orderVolumeBody, opts))
}

/** True when the order-volume API response is for an eligible Starter/Pro tenant. */
export function isOrderVolumeBillingEligible (body) {
  return !!(body && body.eligible === true)
}

/**
 * Sidebar meter + Usage tab — Starter / Pro only; hidden for legacy and daily ASIN billing.
 * @param {object} [opts] — `billingVariant`, or inputs for {@link resolveBillingScreenVariant}
 */
export function shouldShowOrderVolumeUi (packageDetails, orderVolumeBody, opts) {
  opts = opts || {}
  var variant =
    opts.billingVariant != null
      ? opts.billingVariant
      : resolveBillingScreenVariant({
          hasDailyPricing: opts.hasDailyPricing,
          packageDetails: packageDetails,
          billingUserTypeRaw: opts.billingUserTypeRaw,
          billingSummary: opts.billingSummary
        })
  if (isLegacyBillingUserType(opts)) return false
  if (isLegacyOrDailyBillingVariant(variant)) return false
  if (variant !== 'new') return false
  if (!isStarterOrProPackage(packageDetails, orderVolumeBody, opts)) return false
  if (orderVolumeBody && orderVolumeBody.eligible === false) return false
  return true
}

function truthyFlag (value) {
  return value === true || value === 'true' || value === 1 || value === '1'
}

/**
 * Trial tenants should not see monthly order caps (e.g. 2,000) in the UI.
 * @param {object} [opts]
 * @param {object} [opts.orderVolumeBody]
 * @param {object} [opts.packageDetails]
 * @param {number} [opts.paymentStatus] - from `$root.paymentStatus`
 * @param {boolean} [opts.hasPaymentMethodOnFile]
 */
export function isTrialOrderVolumeUser (opts) {
  opts = opts || {}
  var summary = opts.subscriptionSummary
  if (summary && typeof summary === 'object') {
    var status = String(summary.status || '').toLowerCase()
    if (status === 'active' || status === 'past_due') return false
    if (summary.trialExpired) return false
    if (summary.trialActive) return true
    return false
  }
  var body = opts.orderVolumeBody
  if (body) {
    if (truthyFlag(body.trial) || truthyFlag(body.onTrial) || truthyFlag(body.isTrial)) return true
    if (truthyFlag(body.inTrialPeriod) || truthyFlag(body.in_trial_period)) return true
    var plan = body.plan
    if (plan && (truthyFlag(plan.trial) || truthyFlag(plan.onTrial) || truthyFlag(plan.isTrial))) return true
  }
  var pd = opts.packageDetails
  if (pd) {
    if (truthyFlag(pd.trial) || truthyFlag(pd.onTrial) || truthyFlag(pd.inTrial)) return true
    var status = pd.subscriptionStatus || pd.subscription_status || pd.stripeSubscriptionStatus
    if (status && String(status).toLowerCase() === 'trialing') return true
  }
  var ps = opts.paymentStatus
  if ((ps === 0 || ps === 2) && isStarterOrProPackage(pd, body, opts)) return true
  if (opts.hasPaymentMethodOnFile === false && isStarterOrProPackage(pd, body, opts)) return true
  return false
}

/** Trial users and allowlisted tenants — hide quota bars and cap copy. */
export function userHasNoMonthlyOrderCap (opts) {
  return isTrialOrderVolumeUser(opts) || isTenantWithoutOrderCap(opts)
}

export function fallbackMonthlyOrderVolume (packageDetails, periodEndEpoch, opts) {
  opts = opts || {}
  if (!isStarterOrProPackage(packageDetails, opts.orderVolumeBody, opts)) return null
  var ctx = Object.assign({ packageDetails: packageDetails }, opts || {})
  var isTrial = isTrialOrderVolumeUser(ctx)
  var noCap = userHasNoMonthlyOrderCap(ctx)
  var pk = packageKeyFromDetails(packageDetails)
  var tenantId = tenantIdFromOpts(opts)
  var pkgLimit = monthlyOrderLimitForPackageKey(pk, tenantId)
  var flatYearly = isFlatYearlyPlanKey(pk)
  var limit = noCap || flatYearly ? null : (pkgLimit != null ? pkgLimit : 2000)
  var resetsAt = ''
  if (periodEndEpoch) {
    var d = new Date(Number(periodEndEpoch) * 1000)
    if (!isNaN(d.getTime())) {
      resetsAt = d.toISOString().slice(0, 10)
    }
  }
  return {
    used: 0,
    limit: limit,
    remaining: noCap ? null : limit,
    percentUsed: 0,
    resetsAt: resetsAt
  }
}

/** Merge API + fallback quota data; strip caps for trial users. */
export function resolveMonthlyOrderVolumeForDisplay (packageDetails, orderVolumeBody, periodEndEpoch, opts) {
  opts = Object.assign({
    packageDetails: packageDetails,
    orderVolumeBody: orderVolumeBody
  }, opts || {})
  var mov = null
  if (isOrderVolumeBillingEligible(orderVolumeBody) && orderVolumeBody.monthlyOrderVolume) {
    mov = Object.assign({}, orderVolumeBody.monthlyOrderVolume)
  } else {
    mov = fallbackMonthlyOrderVolume(packageDetails, periodEndEpoch, opts)
  }
  if (!mov) return null
  mov = applyTenantProOrderLimit(mov, packageDetails, tenantIdFromOpts(opts))
  if (!userHasNoMonthlyOrderCap(opts)) return mov
  return Object.assign({}, mov, {
    limit: null,
    remaining: null,
    percentUsed: 0
  })
}

export function shouldShowOrderVolumeLimit (monthlyOrderVolume, opts) {
  if (userHasNoMonthlyOrderCap(opts)) return false
  return monthlyOrderVolume != null && monthlyOrderVolume.limit != null
}

/**
 * Normalize API period (ISO date, datetime, or unix seconds) for order-volume query params.
 * @param {'start'|'end'} role — start of day for period start, end of day for period end
 */
export function epochFromPeriodValue (value, role) {
  if (value == null || value === '') return null
  var n = Number(value)
  if (!isNaN(n) && String(value).trim() !== '' && !String(value).includes('-')) {
    if (n > 1e12) n = Math.floor(n / 1000)
    return Math.floor(n)
  }
  var s = String(value).trim()
  var d
  if (s.indexOf('T') >= 0) {
    d = new Date(s)
  } else if (role === 'end') {
    d = new Date(s + 'T23:59:59Z')
  } else {
    d = new Date(s + 'T00:00:00Z')
  }
  if (isNaN(d.getTime())) return null
  return Math.floor(d.getTime() / 1000)
}

/** ~1 Stripe billing month (reject multi-month identity spans like Apr–Jun). */
var CURRENT_BILLING_MONTH_MIN_DAYS = 27
var CURRENT_BILLING_MONTH_MAX_DAYS = 35

/**
 * Current billing month only (e.g. 26 May – 26 Jun), anchored on subscription period end.
 * If identity periodStart spans more than one month, start = one calendar month before period end.
 */
export function currentBillingMonthEpochs (periodStartEpoch, periodEndEpoch) {
  var end = epochFromPeriodValue(periodEndEpoch, 'end')
  if (end == null) return { from: null, to: null }

  var from = null
  if (periodStartEpoch != null && periodStartEpoch !== '') {
    var candidate = epochFromPeriodValue(periodStartEpoch, 'start')
    if (candidate != null && end > candidate) {
      var days = (end - candidate) / 86400
      if (days >= CURRENT_BILLING_MONTH_MIN_DAYS && days <= CURRENT_BILLING_MONTH_MAX_DAYS) {
        from = candidate
      }
    }
  }
  if (from == null) {
    var endDate = new Date(end * 1000)
    var startDate = new Date(
      Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() - 1, endDate.getUTCDate(), 0, 0, 0, 0)
    )
    from = Math.floor(startDate.getTime() / 1000)
  }
  return { from: from, to: end }
}

/** ISO date strings for Usage UI (matches order-volume API window). */
export function currentBillingMonthDateStrings (periodStartEpoch, periodEndEpoch) {
  var range = currentBillingMonthEpochs(periodStartEpoch, periodEndEpoch)
  if (range.from == null || range.to == null) return null
  return {
    periodStart: new Date(range.from * 1000).toISOString().slice(0, 10),
    periodEnd: new Date(range.to * 1000).toISOString().slice(0, 10)
  }
}

/** GET /api/billing/order-volume — always scoped to the current billing month. */
export function orderVolumeApiParams (opts) {
  opts = opts || {}
  var range = currentBillingMonthEpochs(opts.periodStart, opts.periodEnd)
  var params = {}
  if (range.from != null) params.from = range.from
  if (range.to != null) params.to = range.to
  return params
}

export function planBadgeFromPackageOrApi (packageDetails, orderVolumeBody, opts) {
  var ctx = Object.assign({
    packageDetails: packageDetails,
    orderVolumeBody: orderVolumeBody
  }, opts || {})
  if (isTrialOrderVolumeUser(ctx)) {
    return 'TRIAL'
  }
  var plan = orderVolumeBody && orderVolumeBody.plan
  if (plan && plan.name) return String(plan.name).toUpperCase()
  if (plan && plan.packageKey) return String(plan.packageKey).toUpperCase()
  var pk = resolveOrderVolumePlanKey(packageDetails, orderVolumeBody, opts)
  if (pk === 'PRO' || pk === 'PRO_ANNUAL' || pk === 'STARTER' || pk === 'FLAT_YEARLY') return pk
  if (packageDetails) {
    var name = packageDetails.name || packageDetails.packageName
    if (name) return String(name).toUpperCase()
  }
  return 'PLAN'
}



// WEBPACK FOOTER //
// ./src/utils/orderVolumeBilling.js